import { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { FIRESTORE_DB, getPplTalkMessageEurW } from '../firebaseConfig';
import { useAuth } from '../hooks/useAuth';
import { useMessageCache } from '../contexts/MessageCacheContext';
import SEO from '../components/SEO';
import StructuredData, { generateDiscussionThreadSchema, generateBreadcrumbSchema } from '../components/StructuredData';
import MonoAvatar from '../components/MonoAvatar';
import { extractMessageId, slugMatchesTitle, getMessageUrl, getUserProfileUrl, getUserDisplayName } from '../utils/messageUtils';
import LikeButton from '../components/LikeButton';
import ReplyInput from '../components/ReplyInput';
import ShareButton from '../components/ShareButton';
import FlagModal from '../components/FlagModal';
import { formatTimestamp, toDateSafe } from '../utils/dateUtils';
import { ChevronLeft, Flag } from 'react-feather';
import '../styles/Posts.css';

const CATEGORY_COLORS = {
  'just talking': 'violet',
  'relationships': 'pink',
  'personal': 'violet',
  'support': 'cyan',
  'check-ins': 'cyan',
  'wins': 'amber',
  'gratitude': 'amber',
  'advice': 'pink',
  'venting': 'pink',
  'mental health': 'cyan',
  'social connection': 'violet',
  'general': 'violet',
};

export default function MessageDetail() {
  const { messageSlug } = useParams();
  const [message, setMessage] = useState(null);
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const [error, setError] = useState(null);
  const [flagModalOpen, setFlagModalOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getCachedMessages, setCachedMessages, clearAllCache } = useMessageCache();

  const messageId = useMemo(() => extractMessageId(messageSlug), [messageSlug]);

  useEffect(() => {
    if (!messageId) {
      setError('No message ID provided');
      setLoading(false);
      return;
    }

    const loadMessage = async () => {
      try {
        setLoading(true);
        setError(null);

        if (user?.uid) {
          const userDoc = await getDoc(doc(FIRESTORE_DB, 'users', user.uid));
          if (userDoc.exists()) {
            setUserProfile(userDoc.data());
          }
        }

        const cacheParams = { sortMode: 'message-detail', filterCategory: messageId, lastDocId: null };
        const cachedData = getCachedMessages(cacheParams);
        if (cachedData) {
          setMessage(cachedData.message);
          setReplies(cachedData.replies || []);
          if (cachedData.message.title && !slugMatchesTitle(messageSlug, cachedData.message.title)) {
            navigate(getMessageUrl(cachedData.message), { replace: true });
          }
          setLoading(false);
          return;
        }

        const result = await getPplTalkMessageEurW({ messageId });
        if (!result.data.success) throw new Error(result.data.error || 'Failed to load message');

        const { message: messageData, replies: repliesData } = result.data;
        if (!messageData) { setError('Message not found'); return; }

        setMessage(messageData);
        setReplies(repliesData || []);
        setCachedMessages(cacheParams, { message: messageData, replies: repliesData || [] });

        if (messageData.title && !slugMatchesTitle(messageSlug, messageData.title)) {
          navigate(getMessageUrl(messageData), { replace: true });
        }
      } catch (error) {
        console.error('Error loading message:', error);
        if (error.code === 'invalid-argument') setError(`Invalid request: ${error.message}`);
        else if (error.code === 'internal') setError('Server error. Please try again later.');
        else setError('Failed to load message');
      } finally {
        setLoading(false);
      }
    };

    loadMessage();
  }, [messageId, messageSlug, user, navigate, getCachedMessages, setCachedMessages]);

  const baseUrl = 'https://pplkpr.ai';
  const currentUrl = message ? `${baseUrl}${getMessageUrl(message)}` : `${baseUrl}/talk/${messageSlug}`;
  const seoTitle = message?.title || message?.body?.substring(0, 100) || 'Discussion on pplkpr';
  const seoDescription = message?.body?.substring(0, 160) || 'Join the conversation on pplkpr';
  const messageDate = message?.timestamp ? toDateSafe(message.timestamp) : null;
  const publishedTime = messageDate && !isNaN(messageDate.getTime()) ? messageDate.toISOString() : undefined;

  if (loading) return (
    <div className="feed">
      <SEO title="Loading..." description="Loading message" noindex={true} />
      <div className="page-shell">
        <div className="page-shell__inner page-shell__inner--narrow">
          <div className="glass-panel" style={{ padding: 24 }}>
            <div className="skeleton-line wide" style={{ marginBottom: 12 }} />
            <div className="skeleton-line wide" style={{ marginBottom: 8 }} />
            <div className="skeleton-line" style={{ width: '60%' }} />
          </div>
        </div>
      </div>
    </div>
  );

  if (error || !message) return (
    <div className="feed">
      <SEO title="Message Not Found" description="This message could not be found" noindex={true} />
      <div className="page-shell">
        <div className="page-shell__inner page-shell__inner--narrow">
          <button className="up-back" onClick={() => navigate(-1)}>
            <ChevronLeft size={14} />
            <span>back</span>
          </button>
          <div className="glass-panel" style={{ textAlign: 'center', padding: 32 }}>
            <p style={{ color: '#ff6b6b', margin: '0 0 12px' }}>{error || 'Message not found'}</p>
            <button className="primary-button" onClick={() => navigate(-1)}>go back</button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="feed">
      <SEO
        title={seoTitle}
        description={seoDescription}
        url={currentUrl}
        type="article"
        author={getUserDisplayName(message)}
        publishedTime={publishedTime}
        tags={message.categories || []}
      />
      <StructuredData data={generateDiscussionThreadSchema(message, replies, baseUrl)} />
      <StructuredData data={generateBreadcrumbSchema([
        { name: 'Home', url: baseUrl },
        { name: 'Discussions', url: `${baseUrl}/` },
        { name: seoTitle, url: currentUrl }
      ])} />

      <div className="page-shell">
        <div className="page-shell__inner page-shell__inner--narrow">

          {/* Back button */}
          <button className="up-back" onClick={() => navigate(-1)}>
            <ChevronLeft size={14} />
            <span>back</span>
          </button>

          {/* Main post */}
          <div className="post-detail-panel">
            <div className="post-detail-author">
              <MonoAvatar name={getUserDisplayName(message)} size={40} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  className="post-detail-author-name"
                  onClick={() => navigate(getUserProfileUrl(message))}
                  role="button"
                  tabIndex={0}
                  style={{ cursor: 'pointer' }}
                >
                  {message.userName === userProfile?.tetherHandle
                    ? 'posted by me'
                    : getUserDisplayName(message)}
                </div>
                <div className="post-detail-author-time">{formatTimestamp(message.timestamp)}</div>
              </div>
            </div>

            {message.title && <h1 className="post-detail-title">{message.title}</h1>}
            {message.body && <p className="post-detail-body">{message.body}</p>}

            {Array.isArray(message.categories) && message.categories.length > 0 && (
              <div className="post-detail-tags">
                {message.categories.map(c => (
                  <span key={c} className={`tag-pill tag-pill--md tag-pill--${CATEGORY_COLORS[c] || 'violet'}`}>{c}</span>
                ))}
              </div>
            )}

            {/* Reactions */}
            <div className="post-reactions">
              <LikeButton
                messageId={message.id}
                likes={message.likes || 0}
                likedBy={message.likedBy || []}
                onLikeChange={(newLikes, newLikedBy) => {
                  setMessage(prev => ({ ...prev, likes: newLikes, likedBy: newLikedBy }));
                }}
              />
              <ShareButton
                url={getMessageUrl(message)}
                title={message.title || 'Discussion on pplkpr'}
                text={message.body?.substring(0, 100)}
                size="small"
              />
              {user && (
                <button className="post-reaction-btn" onClick={() => setFlagModalOpen(true)} title="Report this post">
                  <Flag size={14} />
                  <span>report</span>
                </button>
              )}
            </div>
          </div>

          {/* Replies section */}
          <div className="post-replies-header">replies · {replies.length}</div>

          {replies.length === 0 ? (
            <div className="glass-panel" style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted)', fontSize: 13 }}>
              no replies yet — be the first to respond
            </div>
          ) : (
            <div className="post-replies-list">
              {replies.map(r => (
                <div key={r.id} className="post-reply-card">
                  <div className="post-reply-header">
                    <MonoAvatar name={getUserDisplayName(r)} size={28} />
                    <span
                      className="post-reply-author"
                      onClick={() => navigate(getUserProfileUrl(r))}
                      role="button"
                      tabIndex={0}
                      style={{ cursor: 'pointer' }}
                    >
                      {getUserDisplayName(r)}
                    </span>
                    <span className="post-reply-time">· {formatTimestamp(r.timestamp)}</span>
                    <div style={{ flex: 1 }} />
                    <LikeButton
                      messageId={message.id}
                      replyId={r.id}
                      likes={r.likes || 0}
                      likedBy={r.likedBy || []}
                      size="small"
                      onLikeChange={(newLikes, newLikedBy) => {
                        setReplies(prev => prev.map(reply =>
                          reply.id === r.id ? { ...reply, likes: newLikes, likedBy: newLikedBy } : reply
                        ));
                      }}
                    />
                  </div>
                  <div className="post-reply-text">{r.text}</div>
                </div>
              ))}
            </div>
          )}

          {/* Reply composer */}
          <div className="post-reply-composer">
            <ReplyInput
              messageId={message.id}
              onReplyCreated={() => { clearAllCache(); window.location.reload(); }}
            />
          </div>

          {/* Flag Modal */}
          <FlagModal isOpen={flagModalOpen} messageId={message.id} onClose={() => setFlagModalOpen(false)} />

        </div>
      </div>
    </div>
  );
}
